import React, { useEffect, useState } from "react";
import { getFirestore, doc, getDoc, collection, query, where, getDocs, collectionGroup, onSnapshot, orderBy, limit } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions"; // Import Firebase Functions
import { useNavigate } from "react-router-dom";
import { logout } from "../firebase/auth"; // Import logout function

const AdminDashboard = () => {
  const [userData, setUserData] = useState(null);
  const navigate = useNavigate();
  const [targetUid, setTargetUid] = useState(""); // State for Firebase UID
  const [showCustomViewer, setShowCustomViewer] = useState(false);
  const [myCustomItems, setMyCustomItems] = useState([]);
  const auth = getAuth(); // you already import getAuth; ensure you have this instance here
  const [newVisitsCount, setNewVisitsCount] = useState(0);
  const [lastSeenVisit, setLastSeenVisit] = useState(null);
  const [newVisitsTotal, setNewVisitsTotal] = useState(0);
  
  const openCustomViewer = async () => {
    try {
      const user = auth.currentUser;
      if (!user) {
        alert("You must be logged in to view your custom requests.");
        return;
      }
      const q = query(collection(getFirestore(), "customContent"), where("userId", "==", user.uid));
      const snap = await getDocs(q);
      setMyCustomItems(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setShowCustomViewer(true);
    } catch (err) {
      console.error("Error loading customContent:", err);
      alert("Could not load your custom requests.");
    }
  };

  
  const assignAdmin = async (uid) => {
    const functions = getFunctions();
    const setAdmin = httpsCallable(functions, "setAdminClaim");

    try {
      const result = await setAdmin({ uid });
      console.log(result.data.message);
      alert("✅ Admin claim set for UID: " + uid);
    } catch (err) {
      console.error("Error setting admin claim:", err);
      alert("❌ Failed to set admin claim. See console for details.");
    }
  };

  const handleNavigation = (path) => {
    navigate(path);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login");
    } catch (error) {
      console.error("Logout error:", error.message);
    }
  };

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;  // wait for auth
  
    const db = getFirestore();
    const ref = collection(db, "users", user.uid, "pageLogs");
  
    const unsub = onSnapshot(ref, (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        if (change.type === "added") {
          const data = change.doc.data();
          const uid = data.userId;
          if (!uid) return;
  
          const list = JSON.parse(localStorage.getItem("newVisitUserIds") || "[]");
  
          if (!list.includes(uid)) {
            list.push(uid);
            localStorage.setItem("newVisitUserIds", JSON.stringify(list));
            setNewVisitsCount(list.length);
          }
        }
      });
    });
  
    return () => unsub();
  }, [auth]);
  
  
  
  
  useEffect(() => {
    const stored = localStorage.getItem("newVisitsCount");
    if (stored) setNewVisitsCount(Number(stored));
  }, []);
  
  useEffect(() => {
    localStorage.setItem("newVisitsCount", newVisitsCount);
  }, [newVisitsCount]);
  
  
  const backgroundImageUrl = userData?.backgroundImageUrl || ""; // or hardcode a URL to test

  const adminCards = [
    { key: "users", label: "Users", icon: "👥", color: "#2563eb", onClick: () => { handleNavigation("/admin/users"); setNewVisitsCount(0); localStorage.setItem("newVisitUserIds", JSON.stringify([])); }, badge: newVisitsCount > 0 ? `${newVisitsCount} users` : null, subBadge: newVisitsCount > 0 ? `(${newVisitsTotal} visits)` : null },
    { key: "tools", label: "Tools", icon: "🛠️", color: "#0891b2", onClick: () => handleNavigation("/admin/tools") },
    { key: "layout", label: "Layout Builder", icon: "🧩", color: "#7c3aed", onClick: () => handleNavigation("/admin/layout-builder") },
    { key: "roles", label: "Roles", icon: "🔑", color: "#dc2626", onClick: () => handleNavigation("/admin/roles") },
    { key: "emails", label: "Email Directory", icon: "📧", color: "#0d9488", onClick: () => handleNavigation("/admin/emails") },
    { key: "settings", label: "Settings", icon: "⚙️", color: "#d97706", onClick: () => handleNavigation("/admin/settings") },
    { key: "products", label: "Manage Products", icon: "🛍️", color: "#4338ca", onClick: () => handleNavigation("/admin/products") },
    { key: "storefront", label: "View Storefront", icon: "🛒", color: "#9333ea", onClick: () => handleNavigation("/products") },
    { key: "custom", label: "Custom Content", icon: "📄", color: "#1e3a8a", onClick: () => handleNavigation("/custom-content") },
    { key: "scheduler", label: "Demo Scheduler", icon: "📅", color: "#6d28d9", onClick: () => handleNavigation("/scheduler") },
    { key: "po", label: "Purchase Orders", icon: "🧾", color: "#059669", onClick: () => handleNavigation("/purchase-orders") },
    { key: "bulk", label: "Bulk Upload Users", icon: "📤", color: "#db2777", onClick: () => handleNavigation("/admin/bulk-upload") },
  ];

  return (
    <div
  style={{
    ...styles.adminContainer,
    ...(backgroundImageUrl
      ? {
          backgroundImage: `url(${backgroundImageUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }
      : {}),
  }}
>
      <style>{`
        .admin-dashboard-card {
          transition: transform 0.18s ease, box-shadow 0.18s ease;
        }
        .admin-dashboard-card:hover {
          transform: translateY(-4px) scale(1.03);
          box-shadow: 0 12px 24px rgba(17, 24, 39, 0.15);
        }
      `}</style>

      {/* Logo */}
      <div style={styles.logoContainer}>
        <img
          src="https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2FSBLLogoBlackTM.png?alt=media&token=2f8fde38-b373-4367-9ade-a7b68bae3b6f"
          alt="Logo"
          style={styles.logo}
        />
      </div>

      {/* Home Button */}
      <div style={styles.homeButtonContainer}>
        <button
          onClick={() => navigate("/dashboard")}
          style={styles.homeButton}
        >
          Home
        </button>
      </div>

      {/* Center Section */}
      <div style={styles.centerContainer}>
        <h1 style={styles.pageTitle}>Admin Portal</h1>
        <p style={styles.pageSubtitle}>Manage every part of Smart Board Learning from one place.</p>
      </div>

      {/* Admin Buttons */}
      <div style={styles.adminButtonContainer}>
        {adminCards.map((card) => (
          <div
            key={card.key}
            className="admin-dashboard-card"
            style={{ ...styles.adminButton, position: "relative" }}
            onClick={card.onClick}
          >
            <div style={{ ...styles.cardIconCircle, backgroundColor: card.color }}>
              <span style={styles.cardIcon}>{card.icon}</span>
            </div>
            <p style={styles.buttonText}>{card.label}</p>
            {card.badge && (
              <div style={styles.cardBadge}>
                <div>{card.badge}</div>
                {card.subBadge && <div style={{ fontSize: "11px", opacity: 0.9 }}>{card.subBadge}</div>}
              </div>
            )}
          </div>
        ))}
      </div>
      <br></br>
      <button style={styles.viewerButton} onClick={openCustomViewer}>
  📄 View My Custom Requests
</button>
<br></br>
      <button style={styles.classroomButton} onClick={() => navigate("/admin/classroom")}>
        Classroom
      </button>
      <br></br> <br></br>
      <button style={styles.leaderboardsButton} onClick={() => navigate("/admin/leaderboards")}>
        📊 View All Leaderboards
      </button>
      <br></br> <br></br>
      <button style={styles.logoutButton} onClick={handleLogout}>
        Logout
      </button>
      <br></br> <br></br>
      <button style={styles.treasureMapButton} onClick={() => navigate("/treasure-map")}>
        🗺️ Go to Treasure Map
      </button>
      
      {/* Admin Assignment Section */}
      <div style={{ marginTop: 40, padding: 20, border: "1px solid #ccc", borderRadius: 8 }}>
        <h3>🔐 Assign Admin Access</h3>
        <p>Enter the <strong>Firebase UID</strong> of the user you want to promote to admin:</p>
        <input
          type="text"
          value={targetUid}
          onChange={(e) => setTargetUid(e.target.value)}
          placeholder="Enter Firebase UID"
          style={{
            padding: "8px",
            width: "300px",
            marginRight: "10px",
            borderRadius: 4,
            border: "1px solid #999",
          }}
        />
        <button onClick={() => assignAdmin(targetUid)} disabled={!targetUid}>
          ✅ Set as Admin
        </button>
      </div>
      {showCustomViewer && (
  <div style={styles.viewerOverlay} onClick={() => setShowCustomViewer(false)}>
    <div style={styles.viewerBox} onClick={e => e.stopPropagation()}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h3 style={{ margin: 0 }}>My Custom Material Requests</h3>
        <button onClick={() => setShowCustomViewer(false)} style={{ cursor: "pointer" }}>✖</button>
      </div>
      <div style={{ marginTop: 12, textAlign: "left", maxHeight: 400, overflowY: "auto" }}>
        {myCustomItems.length === 0 ? (
          <p>No requests yet.</p>
        ) : (
          <ul style={{ paddingLeft: 18 }}>
            {myCustomItems.map(item => (
              <li key={item.id} style={{ marginBottom: 12 }}>
                <div><strong>{item.subject}</strong> — {new Date(item.createdAt).toLocaleString()}</div>
                <div>Style: {item.style}</div>
                <div>Paid: {item.paid ? "Yes" : "No"}</div>
                <details style={{ marginTop: 6 }}>
                  <summary>Details</summary>
                  <div style={{ marginTop: 6 }}>
                    <div><strong>Facts:</strong></div>
                    <pre style={{ whiteSpace: "pre-wrap", margin: 0 }}>{item.facts}</pre>
                    <div style={{ marginTop: 8 }}><strong>Q/A:</strong></div>
                    <pre style={{ whiteSpace: "pre-wrap", margin: 0 }}>{item.qa}</pre>
                  </div>
                </details>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  </div>
)}

    </div>
  );
};

const styles = {
  adminContainer: {
    textAlign: "center",
    padding: "20px",
    backgroundColor: "#f3f4f6",
    minHeight: "100vh",
    fontFamily: "'Segoe UI', Helvetica, Arial, sans-serif",
  },
  logoContainer: {
    display: "flex",
    justifyContent: "center",
    marginBottom: "20px",
  },
  logo: {
    height: "70px",
  },
  homeButtonContainer: {
    display: "flex",
    justifyContent: "center",
    marginBottom: "20px",
  },
  homeButton: {
    cursor: "pointer",
    padding: "9px 20px",
    fontSize: "14px",
    fontWeight: 600,
    border: "none",
    borderRadius: "8px",
    backgroundColor: "#111827",
    color: "#fff",
    boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.15)",
  },
  centerContainer: {
    backgroundColor: "#fff",
    borderRadius: "12px",
    padding: "24px",
    margin: "20px auto",
    maxWidth: "620px",
    boxShadow: "0px 2px 10px rgba(17, 24, 39, 0.08)",
  },
  pageTitle: {
    margin: "0 0 6px",
    fontSize: "26px",
    fontWeight: 700,
    color: "#111827",
  },
  pageSubtitle: {
    margin: 0,
    fontSize: "14px",
    color: "#6b7280",
  },
  adminButtonContainer: {
    display: "flex",
    justifyContent: "center",
    flexWrap: "wrap",
    gap: "16px",
    marginTop: "20px",
    maxWidth: "1000px",
    marginLeft: "auto",
    marginRight: "auto",
  },
  adminButton: {
    width: "150px",
    height: "150px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    cursor: "pointer",
    border: "1px solid #eef0f3",
    borderRadius: "14px",
    backgroundColor: "#ffffff",
    boxShadow: "0px 2px 8px rgba(17, 24, 39, 0.06)",
    padding: "16px 10px",
  },
  cardIconCircle: {
    width: "44px",
    height: "44px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "10px",
  },
  cardIcon: {
    fontSize: "20px",
  },
  buttonText: {
    color: "#1f2937",
    fontSize: "14px",
    fontWeight: 600,
    textAlign: "center",
    margin: 0,
  },
  cardBadge: {
    position: "absolute",
    top: 8,
    right: 8,
    backgroundColor: "#dc2626",
    color: "white",
    fontSize: "11px",
    fontWeight: "bold",
    borderRadius: "8px",
    padding: "3px 8px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 0 6px rgba(0,0,0,0.25)",
  },
  logoutButton: {
    width: "170px",
    height: "50px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    fontSize: "15px",
    fontWeight: "bold",
    color: "#fff",
    backgroundColor: "#dc2626",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    boxShadow: "0px 2px 8px rgba(220, 38, 38, 0.3)",
    marginTop: "30px",
  },
  classroomButton: {
    width: "170px",
    height: "50px",
    marginTop: "16px",
    backgroundColor: "#0891b2",
    color: "#fff",
    fontSize: "15px",
    fontWeight: "bold",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
  },
  leaderboardsButton: {
    width: "170px",
    height: "50px",
    marginTop: "16px",
    backgroundColor: "#6b7280",
    color: "#fff",
    fontSize: "15px",
    fontWeight: "bold",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
  },
  treasureMapButton: {
    width: "170px",
    height: "50px",
    marginTop: "16px",
    backgroundColor: "#2563eb",
    color: "#fff",
    fontSize: "15px",
    fontWeight: "bold",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
  },
  viewerButton: {
    width: "220px",
    height: "44px",
    marginTop: "16px",
    backgroundColor: "#111827",
    color: "#fff",
    fontSize: "14px",
    fontWeight: 600,
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
  },
  viewerOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  },
  viewerBox: {
    background: "#fff",
    borderRadius: "12px",
    padding: "20px",
    width: "480px",
    maxWidth: "92vw",
    textAlign: "left",
  },
};

export default AdminDashboard;
